// ═══════════════════════════════════════════════════════════════════════════════
// Shared "?" help popover — portal panel (settlement / usage membership, etc.)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type PanelPosition = {
  top: number;
  left: number;
};

const DEFAULT_PANEL_WIDTH_PX = 288;
const DEFAULT_PANEL_ESTIMATED_HEIGHT_PX = 280;
const VIEWPORT_EDGE_PADDING_PX = 8;
const PANEL_GAP_PX = 8;

const resolvePanelPosition = (
  triggerRect: DOMRect,
  panelWidth: number,
  panelHeight: number,
): PanelPosition => {
  const pad = VIEWPORT_EDGE_PADDING_PX;
  const maxLeft = Math.max(pad, window.innerWidth - panelWidth - pad);

  const belowTop = triggerRect.bottom + PANEL_GAP_PX;
  const aboveTop = triggerRect.top - panelHeight - PANEL_GAP_PX;
  const fitsBelow = belowTop + panelHeight <= window.innerHeight - pad;
  const top = fitsBelow ? belowTop : Math.max(pad, aboveTop);

  const preferredLeft = triggerRect.left + triggerRect.width / 2 - panelWidth / 2;
  const left = Math.max(pad, Math.min(preferredLeft, maxLeft));

  return { top, left };
};

type HelpPopoverProps = {
  triggerAriaLabel: string;
  panelAriaLabel: string;
  children: ReactNode;
  /** Tailwind width class for the panel (default w-72). */
  panelWidthClassName?: string;
  estimatedPanelHeightPx?: number;
  className?: string;
};

export const HelpPopover = ({
  triggerAriaLabel,
  panelAriaLabel,
  children,
  panelWidthClassName = 'w-72',
  estimatedPanelHeightPx = DEFAULT_PANEL_ESTIMATED_HEIGHT_PX,
  className = '',
}: HelpPopoverProps) => {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition>({ top: 0, left: 0 });

  const handleTogglePanel = useCallback((): void => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback((): void => {
    setOpen(false);
  }, []);

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleTogglePanel();
      }
    },
    [handleClose, handleTogglePanel],
  );

  const updatePanelPosition = useCallback((): void => {
    if (!rootRef.current) {
      return;
    }
    const triggerRect = rootRef.current.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth ?? DEFAULT_PANEL_WIDTH_PX;
    const panelHeight = panelRef.current?.offsetHeight ?? estimatedPanelHeightPx;
    setPanelPosition(resolvePanelPosition(triggerRect, panelWidth, panelHeight));
  }, [estimatedPanelHeightPx]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updatePanelPosition();
    const frameId = window.requestAnimationFrame(() => {
      updatePanelPosition();
    });
    const handleReposition = (): void => {
      updatePanelPosition();
    };
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      handleClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [handleClose, open]);

  const panelNode =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={panelAriaLabel}
            className={`fixed z-[80] max-w-[calc(100vw-1rem)] rounded-2xl border border-gray-200/80 bg-white p-4 text-left shadow-xl dark:border-gray-600 dark:bg-gray-900 ${panelWidthClassName}`}
            style={{ top: panelPosition.top, left: panelPosition.left }}
          >
            {children}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`relative inline-flex shrink-0 ${className}`.trim()}>
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 text-[11px] font-bold text-gray-500 hover:border-primary-500 hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary-400 dark:hover:text-primary-300"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={triggerAriaLabel}
        tabIndex={0}
        onClick={handleTogglePanel}
        onKeyDown={handleTriggerKeyDown}
      >
        ?
      </button>
      {panelNode}
    </div>
  );
};
